import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useContent } from "@/contexts/ContentContext";
import { useLang } from "@/contexts/LanguageContext";
import { LANGS } from "@/i18n";

const field = "vl-input rounded-lg px-4 py-2.5 w-full";
const THEME_KEYS = [
  ["gold", "theme_gold"], ["gold_bright", "theme_gold_light"],
  ["dark_bg", "theme_dark_bg"], ["dark_surface", "theme_dark_card"],
  ["dark_text", "theme_dark_text"], ["dark_muted", "theme_dark_muted"],
  ["light_bg", "theme_light_bg"], ["light_surface", "theme_light_card"],
  ["light_text", "theme_light_text"], ["light_muted", "theme_light_muted"],
];

const Section = ({ title, children }) => (
  <div className="vl-card rounded-xl p-6"><h3 className="font-serif-display text-xl text-[var(--vl-ivory)] mb-4">{title}</h3>{children}</div>
);

export default function ContentPanel() {
  const { t } = useLang();
  const { reload } = useContent();
  const [d, setD] = useState(null);
  const [editLang, setEditLang] = useState("de");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setD(r.data)); }, []);

  const b = () => (d && d[editLang]) || {};
  const updB = (k, v) => setD((p) => ({ ...p, [editLang]: { ...(p[editLang] || {}), [k]: v } }));
  const updList = (k, i, f2, v) => setD((p) => { const arr = [...((p[editLang] || {})[k] || [])]; arr[i] = { ...arr[i], [f2]: v }; return { ...p, [editLang]: { ...p[editLang], [k]: arr } }; });
  const addItem = (k, item) => setD((p) => ({ ...p, [editLang]: { ...(p[editLang] || {}), [k]: [...((p[editLang] || {})[k] || []), item] } }));
  const delItem = (k, i) => setD((p) => ({ ...p, [editLang]: { ...p[editLang], [k]: (p[editLang][k] || []).filter((_, j) => j !== i) } }));
  const updTheme = (tk, v) => setD((p) => ({ ...p, theme: { ...p.theme, [tk]: v } }));
  const updSocial = (sk, v) => setD((p) => ({ ...p, social: { ...p.social, [sk]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { content: d });
      toast.success(t("save"));
      reload();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!d) return <div className="py-16 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>;

  const bucket = b();

  return (
    <div className="space-y-6 pb-24" data-testid="content-panel">
      {/* language selector for content */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs tracking-widest uppercase text-[var(--vl-stone)] mr-1">{t("edit_language")}:</span>
        {LANGS.map((l) => (
          <button key={l.code} onClick={() => setEditLang(l.code)}
            className={`px-4 py-1.5 rounded-full text-xs tracking-widest uppercase border transition-colors ${editLang === l.code ? "border-[var(--vl-gold)] text-[var(--vl-gold)] bg-[rgba(212,175,55,0.08)]" : "border-[var(--vl-border)] text-[var(--vl-stone)]"}`}
            data-testid={`content-lang-${l.code}`}>{l.label}</button>
        ))}
      </div>

      <Section title={t("content_tab")}>
        <div className="space-y-3">
          <input className={field} value={bucket.hero_title || ""} onChange={(e) => updB("hero_title", e.target.value)} placeholder={t("hero_title_label")} data-testid="content-hero-title" />
          <textarea className={field} rows={2} value={bucket.hero_subtitle || ""} onChange={(e) => updB("hero_subtitle", e.target.value)} placeholder={t("hero_subtitle_label")} />
          <input className={field} value={bucket.about_title || ""} onChange={(e) => updB("about_title", e.target.value)} placeholder={t("about_title_label")} />
          <textarea className={field} rows={4} value={bucket.about_text || ""} onChange={(e) => updB("about_text", e.target.value)} placeholder={t("about_text_label")} />
        </div>
      </Section>

      <Section title={t("whyus_title")}>
        {(bucket.whyus || []).map((w, i) => (
          <div key={i} className="flex gap-2 mb-2 items-start">
            <input className={field + " flex-1"} value={w.title} onChange={(e) => updList("whyus", i, "title", e.target.value)} placeholder={t("title_label")} />
            <input className={field + " flex-[2]"} value={w.text} onChange={(e) => updList("whyus", i, "text", e.target.value)} placeholder={t("description_label")} />
            <button onClick={() => delItem("whyus", i)} className="text-red-300 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("whyus", { title: "", text: "" })} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5 mt-1"><Plus size={14} /> {t("add_item")}</button>
      </Section>

      <Section title={t("how_title")}>
        {(bucket.howitworks || []).map((w, i) => (
          <div key={i} className="flex gap-2 mb-2 items-start">
            <input className={field + " flex-1"} value={w.title} onChange={(e) => updList("howitworks", i, "title", e.target.value)} placeholder={t("title_label")} />
            <input className={field + " flex-[2]"} value={w.text} onChange={(e) => updList("howitworks", i, "text", e.target.value)} placeholder={t("description_label")} />
            <button onClick={() => delItem("howitworks", i)} className="text-red-300 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("howitworks", { title: "", text: "" })} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5 mt-1"><Plus size={14} /> {t("add_item")}</button>
      </Section>

      <Section title={t("testimonials_tab")}>
        {(bucket.testimonials || []).map((r, i) => (
          <div key={i} className="flex gap-2 mb-2 items-start" data-testid={`testimonial-${i}`}>
            <input className={field + " w-36"} value={r.name} onChange={(e) => updList("testimonials", i, "name", e.target.value)} placeholder={t("name_label")} />
            <input className={field + " flex-1"} value={r.text} onChange={(e) => updList("testimonials", i, "text", e.target.value)} placeholder={t("description_label")} />
            <input type="number" min="1" max="5" className={field + " w-20"} value={r.rating || 5} onChange={(e) => updList("testimonials", i, "rating", parseInt(e.target.value) || 5)} />
            <button onClick={() => delItem("testimonials", i)} className="text-red-300 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("testimonials", { name: "", text: "", rating: 5 })} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5 mt-1" data-testid="add-testimonial"><Plus size={14} /> {t("add_item")}</button>
      </Section>

      <Section title={t("social_label")}>
        <div className="space-y-3">
          <input className={field} value={d.social?.instagram || ""} onChange={(e) => updSocial("instagram", e.target.value)} placeholder="Instagram URL" />
          <input className={field} value={d.social?.whatsapp || ""} onChange={(e) => updSocial("whatsapp", e.target.value)} placeholder="WhatsApp URL" />
          <input className={field} value={d.social?.facebook || ""} onChange={(e) => updSocial("facebook", e.target.value)} placeholder="Facebook URL" />
        </div>
      </Section>

      <Section title={t("theme_tab")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {THEME_KEYS.map(([k, labelKey]) => (
            <div key={k} className="flex items-center gap-3" data-testid={`theme-${k}`}>
              <input type="color" value={d.theme?.[k] || "#000000"} onChange={(e) => updTheme(k, e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[var(--vl-border)]" />
              <span className="text-sm text-[var(--vl-ivory)] flex-1">{t(labelKey)}</span>
              <input className="vl-input rounded px-2 py-1.5 text-xs w-24" value={d.theme?.[k] || ""} onChange={(e) => updTheme(k, e.target.value)} />
            </div>
          ))}
        </div>
      </Section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="vl-btn-gold rounded-full px-8 py-3 text-sm font-medium flex items-center gap-2 shadow-xl" data-testid="save-content-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("save")}
        </button>
      </div>
    </div>
  );
}
